<section class="component-layer audit-details">
  <div class="header-row">
    <div class="title">Audit log details - {{ entity }}</div>
  </div>

  <ng-container *ngIf="!entity || !entityId">
    <p class="no-params">
      Missing entity or entityId. Go back to
      <a [routerLink]="[PMSRouteEnum.PMS_BASE_URL, 'audit', 'logs']"
        >Audit Logs</a
      >
      and open a row details.
    </p>
  </ng-container>

  <ng-container *ngIf="entity && entityId">
    <!-- Filters -->
    <div class="filter-row d-flex flex-wrap align-items-end gap-2 mb-3">
      <div class="filter-group">
        <p-select
          [options]="actionOptions"
          [(ngModel)]="filterAction"
          optionLabel="label"
          optionValue="value"
          placeholder="Action"
          [showClear]="true"
          class="table-filter filter-select"
          (onChange)="onFilterChange()"
          [style]="{ height: '38px' }"
        />
      </div>
      <div class="filter_checkin_date">
        <p-datepicker
          [(ngModel)]="dateFrom"
          placeholder="Date from"
          [showClear]="true"
          dateFormat="yy-mm-dd"
          class="table-filter"
          (onSelect)="onFilterChange()"
          (onClear)="onFilterChange()"
        />
      </div>
      <div class="filter_checkin_date">
        <p-datepicker
          [(ngModel)]="dateTo"
          placeholder="Date to"
          [showClear]="true"
          dateFormat="yy-mm-dd"
          class="table-filter"
          (onSelect)="onFilterChange()"
          (onClear)="onFilterChange()"
        />
      </div>
      <div
        class="d-flex filter-reset align-items-center"
        (click)="clearFilters()"
      >
        <img
          src="/images/icons/rotate-clockwise.svg"
          alt="Reset"
          class="reset-icon"
        />
        <span class="reset-text">Reset Filter</span>
      </div>
    </div>

    <div *ngIf="loading" class="loading-wrap">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
    </div>

    <div *ngIf="!loading && groups.length === 0" class="empty-wrap">
      No audit entries found for this entity.
    </div>

    <!-- Timeline -->
    <div *ngIf="!loading && groups.length > 0" class="timeline">
      <div *ngFor="let group of groups" class="timeline-group">
        <div class="timeline-date-label">{{ group.label }}</div>
        <div class="timeline-date-value">{{ group.date }}</div>

        <div class="timeline-track">
          <div
            *ngFor="let log of group.logs; let last = last"
            class="timeline-item"
          >
            <div class="timeline-node"></div>
            <div *ngIf="!last" class="timeline-line"></div>

            <div class="timeline-card">
              <div class="timeline-card-header" (click)="toggleExpand(log.id)">
                <span class="timeline-time">{{
                  TimeFormatterIso.format(log.createdAt)
                }}</span>
                <span
                  class="timeline-action action-badge"
                  [class]="'action-' + (log.action || '').toLowerCase()"
                  >{{ log.action }}</span
                >
                <div class="timeline-actor actor-cell">
                  <div class="actor-avatar">
                    @if (getProfileImageUrl(log?.actor?.profileImg)) {
                      <img
                        [src]="getProfileImageUrl(log.actor.profileImg)"
                        alt="Actor"
                        (error)="onAvatarError($event)"
                      />
                    }
                    <span
                      class="i-circle avatar-fallback"
                      [style.display]="getProfileImageUrl(log?.actor?.profileImg) ? 'none' : 'inline-flex'"
                      >{{ getNameInitial(log?.actor) }}</span>
                  </div>
                  <span class="actor-name">{{ getActorDisplay(log) }}</span>
                </div>
                <span class="timeline-message text-truncate">{{
                  log.message || "—"
                }}</span>
                <mat-icon class="expand-icon">{{
                  isExpanded(log.id) ? "expand_less" : "expand_more"
                }}</mat-icon>
              </div>

              <div class="timeline-card-body" *ngIf="isExpanded(log.id)">
                <div *ngIf="log.changes?.summary" class="change-summary">
                  Summary: {{ log.changes?.summary }}
                </div>
                <div
                  *ngIf="(log.changes?.attributeChanges?.length ?? 0) > 0"
                  class="attribute-changes"
                >
                  <div class="attr-title">Changes</div>
                  <div
                    *ngFor="let ch of log.changes?.attributeChanges ?? []"
                    class="attr-row"
                  >
                    <span class="attr-name">{{ ch.attribute }}</span>
                    <span class="attr-from">{{ ch.from }}</span>
                    <span class="attr-arrow">→</span>
                    <span class="attr-to">{{ ch.to }}</span>
                  </div>
                </div>
                <div
                  *ngIf="log.beforeData && log.afterData"
                  class="before-after"
                >
                  <div class="ba-section">
                    <div class="ba-title">Before</div>
                    <pre class="ba-json">{{ log.beforeData | json }}</pre>
                  </div>
                  <div class="ba-section">
                    <div class="ba-title">After</div>
                    <pre class="ba-json">{{ log.afterData | json }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination (datatable-style with limit chooser) -->
    <div
      *ngIf="!loading && total > 0"
      class="pagination-wrap datatable-paginator"
    >
      <div class="pagination-left">
        <span class="rows-label">Rows per page:</span>
        <p-select
          [options]="rowsPerPageOptions"
          [(ngModel)]="limit"
          optionLabel="label"
          optionValue="value"
          (onChange)="onLimitChange($event.value)"
          styleClass="limit-select"
        />
        <span class="page-report">{{ pageReport }}</span>
      </div>
      <div class="pagination-buttons">
        <button
          type="button"
          class="btn btn-primary-border paginator-btn"
          [disabled]="page <= 1"
          (click)="goToPage(1)"
          pTooltip="First"
        >
          <mat-icon>first_page</mat-icon>
        </button>
        <button
          type="button"
          class="btn btn-primary-border paginator-btn"
          [disabled]="page <= 1"
          (click)="goToPage(page - 1)"
          pTooltip="Previous"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>
        <span class="page-nums">Page {{ page }} of {{ totalPages }}</span>
        <button
          type="button"
          class="btn btn-primary-border paginator-btn"
          [disabled]="page >= totalPages"
          (click)="goToPage(page + 1)"
          pTooltip="Next"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
        <button
          type="button"
          class="btn btn-primary-border paginator-btn"
          [disabled]="page >= totalPages"
          (click)="goToPage(totalPages)"
          pTooltip="Last"
        >
          <mat-icon>last_page</mat-icon>
        </button>
      </div>
    </div>
  </ng-container>
</section>
